import c3 from "c3";
import d3 from "d3";

import { AreaLabelConfiguration, AxisConfiguration, ChartDataPoint, DefaultChartData } from "@/Patient/Models/GrowthCharts/ChartData";
import { C3Chart, C3ContentData } from "./C3Chart";
import { IGrowthChartsCustomAreaHandler } from "./IGrowthChartsCustomAreaHandler";

interface AxisLine {
    label: string;
    value: number;
    className?: string;
}

interface IGrowthChartGraph {
    readonly id: string;
    readonly xAxis: AxisConfiguration;
    readonly yAxis: AxisConfiguration;
    readonly primary: IGrowthChartGraphConfig;
    readonly secondary?: IGrowthChartGraphConfig;
    readonly hideXAxisGrid: boolean;
    readonly hasCustomRegions: boolean;
    readonly growthChartsCustomAreaHandler: IGrowthChartsCustomAreaHandler;
    getLines: () => AxisLine[];
    getRegionLabels: () => AreaLabelConfiguration[];
}

interface IGrowthChartGraphConfig {
    name: { xName: string, yName: string };
    color: string;
    dataPoints: ChartDataPoint[];
}

interface ICurveChartProps {
    defaultPercentile: DefaultChartData;
    chart: IGrowthChartGraph;
    showPrimary?: boolean;
    showSecondary?: boolean;
    onDataPointHover?: (data: ChartDataPoint) => void;
    onDataPointClick?: (id: string) => void;
}

class GrowthChartGraph extends C3Chart<ICurveChartProps, never> {
    public shouldComponentUpdate(nextProps: ICurveChartProps): boolean {
        return !this.props.defaultPercentile.equals(nextProps.defaultPercentile)
            || this.props.chart !== nextProps.chart
            || this.props.showPrimary !== nextProps.showPrimary
            || this.props.showSecondary !== nextProps.showSecondary;
    }

    protected renderGraph(): void {
        super.renderGraph();
    }

    protected onRendered(): void {
        super.onRendered();
        this.displayLegendBaseOnCurve();
        if (this.props.chart.hasCustomRegions) {
            this.displayAreaLabels();
            this.displayAreas();
        }
    }

    protected getControlId(): string {
        return this.props.chart?.id;
    }

    protected getData(): c3.Data {
        let data = this.buildDefaultData();
        if (this.props.chart.primary && this.props.showPrimary)
            data = this.createDataWithChartConfig(data, this.props.chart.primary);

        if (this.props.chart.secondary && this.props.showSecondary)
            data = this.createDataWithChartConfig(data, this.props.chart.secondary);

        return data;
    }

    protected getAxis(): c3.Axis {
        return {
            x: this.getXAxisConfig(),
            y: this.getYAxisConfig()
        };
    }

    protected getGrid(): c3.Grid {
        if (!this.props.chart.getLines()?.length)
            return super.getGrid();

        const baseLines = this.props.chart.getLines();
        const lines: c3.LineOptions[] = baseLines.map(p => {
            let option: c3.LineOptions = {
                value: p.value,
                position: "start",
                class: p.className,
                text: p.label
            };
            return option;
        });

        return {
            x: {
                show: !this.props.chart.hideXAxisGrid,
                lines: lines
            },
            y: { show: true }
        };
    }

    protected getTooltip(): c3.TooltipOptions {
        return {
            contents: (d: C3ContentData[]) => {
                if (!d?.length)
                    return "";

                const chartDataPoint = this.handleTooltipChange(d[0]);
                return chartDataPoint?.tooltipValue ?? "";
            }
        };
    }

    private buildDefaultData(): c3.Data {
        const defaultPoint = this.props.defaultPercentile;
        return {
            xs: defaultPoint.toC3XS(),
            columns: defaultPoint.toC3Columns(),
            types: defaultPoint.toC3Types(),
            colors: defaultPoint.toC3Colors(),
            classes: defaultPoint.toC3Classes(),
            names: defaultPoint.toC3Names(),
            onmouseout: () => this.props.onDataPointHover?.(null)
        };
    }

    private getPointDataByValue(data: C3ContentData): ChartDataPoint {
        let foundPoint: ChartDataPoint = null;
        if (data.id === this.props.chart.primary.name.yName) {
            foundPoint = this.props.chart.primary.dataPoints.firstOrDefault(p => p.x === data.x.toString() && p.y === data.value.toString());
        }
        else if (data.id === this.props.chart.secondary?.name.yName) {
            foundPoint = this.props.chart.secondary.dataPoints.firstOrDefault(p => p.x === data.x.toString() && p.y === data.value.toString());
        }

        return foundPoint;
    }

    private handleTooltipChange(data: C3ContentData): ChartDataPoint {
        const foundPoint = this.getPointDataByValue(data);
        this.props.onDataPointHover?.(foundPoint);

        return foundPoint;
    }

    private createDataWithChartConfig(data: c3.Data, chartConfig: IGrowthChartGraphConfig): c3.Data {
        const chartData = { ...data };
        const dataPoints = chartConfig?.dataPoints?.filter(p => !isNaN(parseFloat(p.x)) && !isNaN(parseFloat(p.y)));

        chartData.xs[chartConfig.name.yName] = chartConfig.name.xName;
        chartData.columns.push(([chartConfig.name.xName] as any[]).concat(dataPoints?.map(p => p.x) ?? []));
        chartData.columns.push(([chartConfig.name.yName] as any[]).concat(dataPoints?.map(p => p.y) ?? []));
        chartData.types[chartConfig.name.yName] = "scatter";
        chartData.colors[chartConfig.name.yName] = chartConfig.color;
        chartData.classes[chartConfig.name.yName] = "clear-opac";
        if (this.props.onDataPointClick)
            chartData.onclick = point => this.handleDataPointClick(point);

        chartData.onmouseover = (pointData: C3ContentData) => {
            if (pointData.id?.contains("primary") || pointData.id?.contains("secondary"))
                this.setPointerCursor();
            else
                this.setDefaultCursor();
        };

        chartData.onmouseout = () => {
            this.setDefaultCursor();
        };
        return chartData;
    }

    private handleDataPointClick(data: C3ContentData): void {
        const pointData = this.getPointDataByValue(data);
        if (pointData?.medicalItemId && this.props.onDataPointClick)
            this.props.onDataPointClick(pointData.medicalItemId);
    }

    private getXAxisConfig(): c3.XAxisConfiguration {
        const xAxis = this.props.chart.xAxis;
        if (!xAxis)
            return null;

        return {
            label: {
                text: xAxis.label,
                position: "outer-center"
            },
            min: xAxis.min,
            max: xAxis.max,
            tick: {
                centered: true,
                values: this.getInterval(xAxis.min, xAxis.max, xAxis.interval)
            }
        };
    }

    private getYAxisConfig(): c3.YAxisConfiguration {
        const yAxis = this.props.chart.yAxis;
        if (!yAxis)
            return null;

        return {
            label: {
                text: yAxis.label,
                position: "outer-middle"
            },
            min: yAxis.min,
            max: yAxis.max,
            tick: {
                values: this.getInterval(yAxis.min, yAxis.max, yAxis.interval)
            }
        };
    }

    private getInterval(min: number, max: number, interval: number): number[] {
        const intervals = [];
        for (let i = min; i <= max; i += interval)
            intervals.push(i);

        return intervals;
    }

    private displayLegendBaseOnCurve(): void {
        const self = this;

        let prevTop = 0;
        let prevRecTop = 0;
        const names = this.props.defaultPercentile?.toC3Names() ?? {};
        const legend = d3.select(`#legend_${self.getControlId()}`);
        legend.selectAll("*").remove();
        legend
            .insert("div", ".chart")
            .attr("class", "legend").selectAll("span")
            .data(Object.keys(names).map(key => key).reverse())
            .enter().append("span")
            .attr("data-id", key => key)
            .html(key => names[key])
            .on("mouseover", key => self.chartApi.focus(key))
            .on("mouseout", () => self.chartApi.revert())
            .each(function(key: string): void {
                const element = self.chartApi.element;
                const target = key.replaceAll("_", "-");

                const lineRec = element.querySelector<SVGElement>(`.c3-chart-line .c3-lines-${target}`).getBoundingClientRect();
                const chartRec = element.getBoundingClientRect();

                const currentRecTop = lineRec.top - chartRec.top - 12;
                const height = currentRecTop - prevRecTop - 20;
                const top = prevTop > 0 ? prevTop + height : currentRecTop;

                d3.select(this).style({
                    "color": self.chartApi.color(key),
                    "top": `${top}px`,
                    "position": "relative"
                });
                prevTop = top;
                prevRecTop = currentRecTop;
            });
    }

    private setPointerCursor(): void {
        this.chartApi?.element?.classList?.remove("default-cursor");
        this.chartApi?.element?.classList?.add("pointer-cursor");
    }

    private setDefaultCursor(): void {
        this.chartApi?.element?.classList?.remove("pointer-cursor");
        this.chartApi?.element?.classList?.add("default-cursor");
    }

    private displayAreaLabels(): void {
        const rectNode = d3.select(`#${this.chartApi.element.parentElement.id} svg g rect`).property("attributes");
        const rectWidth = Number(d3.select(d3.select(rectNode).property("width")).property("value"));
        const rectHeight = Number(d3.select(d3.select(rectNode).property("height")).property("value"));
        const xStepSize = rectWidth / (this.props.chart.xAxis.max - this.props.chart.xAxis.min);
        const yStepSize = rectHeight / (this.props.chart.yAxis.max - this.props.chart.yAxis.min);
        this.props.chart.getRegionLabels().forEach(label => {
            d3.selectAll(`.${label.class}`).remove();
            d3.select(`#${this.chartApi.element.parentElement.id} svg g`)
                .append("text")
                .attr("x", xStepSize * (label.x - this.props.chart.xAxis.min))
                .attr("y", yStepSize * (this.props.chart.yAxis.max - label.y))
                .attr("text-anchor", "start")
                .style("font-size", 12)
                .attr("font-weight", 500)
                .style("fill", label.color)
                .attr("class", label.class)
                .text(label.label);
        });
    }

    private displayAreas(): void {
        this.props.chart.growthChartsCustomAreaHandler?.applyCustomArea(
            this.chartApi.element.parentElement.id,
            this.props.chart,
            this.getData(),
            (this.chartApi as any).internal.x,
            (this.chartApi as any).internal.y
        );
    }
}

export { AxisLine, GrowthChartGraph, IGrowthChartGraph, IGrowthChartGraphConfig };
